import {FC, useState} from 'react';
import {Box, Button, TextField} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {getErrorMessageFromApiError} from '@shared/api';
import {SportApisportsRefDTO} from '../../api/types';
import {useUpdateMapping} from '../../apiHooks';

type Props = {
  categorySlug?: string;
  entityId?: string;
  // The mapping returned by the provider-metadata request (id + name only).
  initial: SportApisportsRefDTO | null;
  label?: string;
};

export const ApiSportsMatchField: FC<Props> = ({
  categorySlug,
  entityId,
  initial,
  label = 'APISports',
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {mutateAsync: updateMapping, isPending: isSaving} = useUpdateMapping();
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const startEditing = () => {
    setInputValue(initial ? String(initial.id) : '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    const id = Number(inputValue);
    if (!categorySlug || !entityId || !inputValue.trim() || Number.isNaN(id)) {
      return;
    }

    try {
      await updateMapping({categorySlug, kind: 'matches', entityId, id});
      enqueueSnackbar('Mapping updated successfully', {variant: 'success'});
      setIsEditing(false);
    } catch (err) {
      const errorMessage = isAxiosError(err)
        ? getErrorMessageFromApiError(err)
        : '';
      enqueueSnackbar(`Could not update mapping. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  if (isEditing) {
    return (
      <Box display="flex" alignItems="center" gap={1} mt={2.5}>
        <TextField
          type="number"
          size="small"
          label={label}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          autoFocus
        />
        <Button onClick={handleSave} disabled={isSaving}>
          Save
        </Button>
        <Button
          variant="outlined"
          onClick={() => setIsEditing(false)}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <Button variant="outlined" onClick={startEditing}>
      Edit
    </Button>
  );
};
